import { useCallback, useEffect, useState } from 'react';
import { IconCard, IconReceipt } from '../../components/Icons';
import { Link } from 'react-router-dom';
import { toApiError, type ApiError } from '../../api/client';
import { billingApi } from '../../api/endpoints';
import type { PagedResult, Payment, Subscription } from '../../api/types';
import { PageHeader, Pagination, StatCard } from '../../components/Common';
import { EmptyState, ErrorState, InlineError, SimulatedPaymentNotice, TableSkeleton } from '../../components/States';
import { date, dateTime, humanise, money } from '../../utils/format';

export function OwnerBillingPage() {
  const [subscriptions, setSubscriptions] = useState<Subscription[] | null>(null);
  const [payments, setPayments] = useState<PagedResult<Payment> | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [subs, pays] = await Promise.all([billingApi.subscriptions(), billingApi.payments({ page, pageSize: 20 })]);

      setSubscriptions(subs);
      setPayments(pays);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <TableSkeleton rows={6} columns={5} />;
  if (error) return <ErrorState error={error} onRetry={load} />;

  const live = subscriptions?.filter(
    (s) => s.propertyId && (s.status === 'Active' || s.status === 'ExpiringSoon'),
  ) ?? [];
  const expiringSoon = live.filter((s) => s.status === 'ExpiringSoon');

  // Only money that actually moved is totalled. Simulated development payments are
  // counted separately and never mixed into the figure.
  const realPayments = payments?.items.filter((p) => !p.isSimulated && p.status === 'Paid') ?? [];
  const simulatedCount = payments?.items.filter((p) => p.isSimulated).length ?? 0;
  const paidTotal = realPayments.reduce((total, p) => total + p.totalAmount, 0);

  return (
    <>
      <PageHeader
        title="Billing &amp; payments"
        description="Subscriptions, listing fees and every payment on record."
      />

      <InlineError message={error} />

      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3">
          <StatCard value={live.length} label="Live subscriptions" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard value={expiringSoon.length} label="Expiring soon" alert={expiringSoon.length > 0} />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard value={money(paidTotal)} label="Paid on this page" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard value={payments?.totalCount ?? 0} label="Payments on record" />
        </div>
      </div>

      {simulatedCount > 0 && (
        <div className="mb-3">
          <SimulatedPaymentNotice>
            {simulatedCount} payment{simulatedCount === 1 ? '' : 's'} on this page {simulatedCount === 1 ? 'was' : 'were'}{' '}
            created by the development gateway. No money changed hands, and they are excluded from the totals above.
          </SimulatedPaymentNotice>
        </div>
      )}

      <section className="mb-4">
        <h3 className="sf-section-title">Subscriptions</h3>

        {subscriptions && subscriptions.length === 0 ? (
          <EmptyState
            icon={<IconCard />}
            title="No subscriptions yet"
            description="PG and hotel branches need a subscription before they can be published."
            action={
              <Link to="/owner/listings" className="btn btn-primary">
                Go to your listings
              </Link>
            }
          />
        ) : (
          <div className="sf-table-wrap">
            <table className="sf-table">
              <thead>
                <tr>
                  <th scope="col">Branch</th>
                  <th scope="col">Plan</th>
                  <th scope="col">Status</th>
                  <th scope="col">Period</th>
                  <th scope="col">Paid</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {subscriptions?.map((subscription) => (
                  <tr key={subscription.id}>
                    <td>
                      {subscription.propertyId
                        ? <Link to={`/owner/listings/${subscription.propertyId}`}>{subscription.propertyTitle}</Link>
                        : subscription.propertyTitle}
                    </td>
                    <td>{subscription.planName}</td>
                    <td>
                      <span
                        className={`sf-pill ${
                          subscription.status === 'Active'
                            ? 'sf-pill--ok'
                            : subscription.status === 'ExpiringSoon'
                              ? 'sf-pill--warn'
                              : 'sf-pill--muted'
                        }`}
                      >
                        {subscription.propertyId ? humanise(subscription.status) : 'Branch deleted'}
                      </span>
                    </td>
                    <td className="small">
                      {date(subscription.startUtc)} – {date(subscription.expiryUtc)}
                      {subscription.daysRemaining !== undefined && subscription.daysRemaining >= 0 && (
                        <div className="sf-muted">{subscription.daysRemaining} days left</div>
                      )}
                    </td>
                    <td>
                      {money(subscription.totalAmount)}
                      <div className="sf-muted small">
                        {money(subscription.planAmount)} + {money(subscription.taxAmount)} tax
                      </div>
                    </td>
                    <td>
                      {subscription.propertyId && (
                        <Link
                          to={
                            subscription.status === 'Active'
                              ? `/owner/listings/${subscription.propertyId}`
                              : `/owner/listings/${subscription.propertyId}?tab=billing`
                          }
                          className={`btn btn-sm ${
                            subscription.status === 'Active' ? 'btn-outline-secondary' : 'btn-outline-primary'
                          }`}
                        >
                          {subscription.status === 'Expired'
                            ? 'Renew'
                            : subscription.status === 'ExpiringSoon'
                              ? 'Renew early'
                              : 'Manage'}
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="form-text">
          Each branch is billed on its own. A lapse on one never affects the others. Deleted branch subscriptions remain
          in this history but are not counted as live.
        </p>
      </section>

      <section>
        <h3 className="sf-section-title">Payment history</h3>

        {payments && payments.items.length === 0 ? (
          <EmptyState icon={<IconReceipt />} title="No payments yet" />
        ) : (
          <>
            <div className="sf-table-wrap">
              <table className="sf-table">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">For</th>
                    <th scope="col">Receipt</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments?.items.map((payment) => (
                    <tr key={payment.id}>
                      <td className="small">{dateTime(payment.paidAtUtc ?? payment.createdAtUtc)}</td>
                      <td>
                        {payment.propertyTitle ?? '—'}
                        <div className="sf-muted small">{humanise(payment.purpose)}</div>
                      </td>
                      <td className="small">{payment.receiptNumber ?? '—'}</td>
                      <td>
                        {money(payment.totalAmount)}
                        <div className="sf-muted small">incl. {money(payment.taxAmount)} tax</div>
                      </td>
                      <td>
                        <span
                          className={`sf-pill ${
                            payment.status === 'Paid'
                              ? 'sf-pill--ok'
                              : payment.status === 'Failed'
                                ? 'sf-pill--warn'
                                : 'sf-pill--muted'
                          }`}
                        >
                          {humanise(payment.status)}
                        </span>

                        {payment.isSimulated && (
                          <div>
                            <span className="sf-pill sf-pill--warn mt-1">Simulated</span>
                          </div>
                        )}

                        {payment.failureReason && <div className="sf-muted small mt-1">{payment.failureReason}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {payments && <Pagination result={payments} onPageChange={setPage} />}
          </>
        )}
      </section>
    </>
  );
}
